import { ProjectsDashboardQueryParams } from "../../api"
import { Grid, makeStyles, Theme, Typography } from "@material-ui/core"
import { ProjectsDashboardViewModel } from "../../api/projects-dashboard"
import { APP_MAX_WIDTH, COLOR_GRAY } from "../../../constants"
import { useIsSmallScreen } from "../../../hooks/use-is-small-screen"
import { useTranslations } from "./useTranslations"
import UpcomingProjectsOverview from "./UpcomingProjectsOverview"
import ResourceSelectionSection from "./ResourceSelectionSection"
import CompletedProjectsOverview from "./CompletedProjectsOverview"
import CompletedProjectsFilter from "./CompletedProjectsFilter"
import React from "react"
import { getProjectQueryFromContext } from "../../../context-options/utils"
import { useAppContext } from "../../../app-context/use-app-context"
import { useRouter } from "../../../hooks"

type ProjectsDashboardProps = {
  dashboard: ProjectsDashboardViewModel
  handleUpdateQuery: (query: ProjectsDashboardQueryParams) => void
}

const useProjectsDashboard = (props: ProjectsDashboardProps) => {
  const { query: routerQuery } = useRouter()
  const { dashboard, handleUpdateQuery } = props
  const { appContext } = useAppContext()
  const { mainContext, subContext } = appContext
  const [currentQuery, setCurrentQuery] = React.useState<ProjectsDashboardQueryParams>({
    ...getProjectQueryFromContext(subContext || mainContext),
  })

  React.useEffect(() => {
    if (routerQuery) {
      setCurrentQuery({
        ...getProjectQueryFromContext(subContext || mainContext),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerQuery])

  const onResourcesSelectionChange = (query: ProjectsDashboardQueryParams) => {
    let newQuery = {
      ...query,
    }
    if (currentQuery.completedProjectsStartDate) {
      newQuery.completedProjectsStartDate = currentQuery.completedProjectsStartDate
    }
    if (currentQuery.completedProjectsEndDate) {
      newQuery.completedProjectsEndDate = currentQuery.completedProjectsEndDate
    }
    if (currentQuery.completedProjectsPeriodType) {
      newQuery.completedProjectsPeriodType = currentQuery.completedProjectsPeriodType
    }
    setCurrentQuery(newQuery)
    handleUpdateQuery(newQuery)
  }

  const onCompletedProjectsFilterChange = (filter: { startDate: string; endDate: string }) => {
    const newQuery: ProjectsDashboardQueryParams = {
      ...currentQuery,
      completedProjectsStartDate: filter.startDate,
      completedProjectsEndDate: filter.endDate,
    }
    setCurrentQuery(newQuery)
    handleUpdateQuery(newQuery)
  }

  const onBarGraphPeriodTypeChange = (props: { periodType: "week" | "month" | "day" }) => {
    const newQuery: ProjectsDashboardQueryParams = {
      ...currentQuery,
      completedProjectsPeriodType: props.periodType,
    }
    setCurrentQuery(newQuery)
    handleUpdateQuery(newQuery)
  }

  return {
    dashboard,
    currentQuery,
    onResourcesSelectionChange,
    onCompletedProjectsFilterChange,
    onBarGraphPeriodTypeChange,
  }
}

const ProjectsDashboard = ({ dashboard, handleUpdateQuery }: ProjectsDashboardProps) => {
  const isSmallScreen = useIsSmallScreen()
  const classes = useStyles({ isSmallScreen })
  const translations = useTranslations()
  const { onResourcesSelectionChange, onCompletedProjectsFilterChange, onBarGraphPeriodTypeChange } =
    useProjectsDashboard({
      dashboard,
      handleUpdateQuery,
    })

  return (
    <div>
      <section className={classes.paper}>
        <section className={classes.section}>
          <ResourceSelectionSection onSelectionChange={onResourcesSelectionChange} />
        </section>
        <section className={classes.section}>
          <Typography variant="h5">{translations.upcomingProjectsSectionHeader}</Typography>
        </section>
        <section className={classes.section}>
          <UpcomingProjectsOverview projectsDashboard={dashboard} />
        </section>
        <section className={classes.completedProjectSection}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5">{translations.completedProjectsSectionHeader}</Typography>
            </Grid>
            <Grid item>
              <CompletedProjectsFilter onChange={onCompletedProjectsFilterChange} />
            </Grid>
          </Grid>
        </section>
        <section className={classes.section}>
          <CompletedProjectsOverview
            projectsDashboard={dashboard}
            onBarGraphPeriodTypeChange={onBarGraphPeriodTypeChange}
          />
        </section>
      </section>
    </div>
  )
}

type StyleProps = {
  isSmallScreen: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  viewContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  paper: ({ isSmallScreen }) => ({
    padding: isSmallScreen ? theme.spacing(0.5) : theme.spacing(2),
    border: isSmallScreen ? "none" : `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
    maxWidth: APP_MAX_WIDTH,
    width: "100%",
  }),
  section: {
    paddingBottom: theme.spacing(3),
  },
  completedProjectSection: {
    marginBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
  },
  card: {
    height: "100%",
  },
  cardHeader: {
    color: COLOR_GRAY,
    fontSize: "1rem",
  },
  cardContent: {
    fontWeight: 400,
  },
  pieChart: {
    padding: theme.spacing(0, 5),
  },
}))

export default ProjectsDashboard
